import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import { debounce } from 'lodash';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';

import { FORTA_BLUE, FORTA_GRAY3, FORTA_GRAY5 } from '../../utils/colors';
import FHConfirmDialog from './dialogs/FHConfirmDialog';
import TrashIcon from '../svgs/TrashIcon';

import type { Moment } from 'moment';

enum TimeUpdateAction {
  ADD = 'ADD',
  SUBTRACT = 'SUBTRACT',
}

interface FHTimeTagProps {
  id: number;
  time: Moment | null;
  minTime: Moment;
  maxTime: Moment;
  containerStyles?: React.CSSProperties;
  debounceTime?: number;
  disabled?: boolean;
  onDelete: (id: number) => void;
  onUpdate: (time: Moment) => void;
}

function FHTimeTag({
  id,
  time,
  minTime,
  maxTime,
  containerStyles = {},
  debounceTime = 500,
  disabled = false,
  onDelete,
  onUpdate,
}: FHTimeTagProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<Moment | null>(
    time && moment.isMoment(time) ? time.clone().startOf('second') : null,
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced onUpdate to prevent flooding the server with requests
  const debouncedOnUpdate = useCallback(
    debounce((newTime: Moment) => {
      if (moment.isMoment(newTime)) {
        onUpdate(newTime);
      }
    }, debounceTime),
    [onUpdate],
  );

  // Clean up debounce on component unmount
  useEffect(() => {
    return () => {
      debouncedOnUpdate.cancel();
    };
  }, [debouncedOnUpdate]);

  // Sync currentTime with the time prop when it changes
  useEffect(() => {
    if (time && moment.isMoment(time)) {
      let newTime = time.clone().startOf('second');
      if (newTime.isSameOrBefore(minTime)) newTime = minTime.clone().add(1, 'second');
      if (newTime.isSameOrAfter(maxTime)) newTime = maxTime.clone().subtract(1, 'second');
      setCurrentTime(newTime);
    }
  }, [time, minTime, maxTime]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setIsEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update time and trigger debounced update
  const updateTime = useCallback(
    (newTime: Moment) => {
      if (!moment.isMoment(newTime)) return;
      let adjustedTime = newTime.clone();
      if (adjustedTime.isSameOrBefore(minTime)) adjustedTime = minTime.clone().add(1, 'second');
      if (adjustedTime.isSameOrAfter(maxTime)) adjustedTime = maxTime.clone().subtract(1, 'second');
      setCurrentTime(adjustedTime);
      debouncedOnUpdate(adjustedTime);
    },
    [minTime, maxTime, debouncedOnUpdate],
  );

  const handleChangeHour = useCallback(
    (action: TimeUpdateAction) => {
      if (!currentTime || !moment.isMoment(currentTime)) return;
      let updatedTime = currentTime.clone();
      if (action === TimeUpdateAction.ADD) {
        updatedTime.add(1, 'hour');
        if (updatedTime.isSameOrAfter(maxTime)) {
          updatedTime = maxTime.clone().subtract(1, 'second');
        }
      } else {
        updatedTime.subtract(1, 'hour');
        if (updatedTime.isSameOrBefore(minTime)) {
          updatedTime = minTime.clone().add(1, 'second');
        }
      }
      updateTime(updatedTime);
    },
    [currentTime, minTime, maxTime, updateTime],
  );

  const handleChangeMinutes = useCallback(
    (action: TimeUpdateAction) => {
      if (!currentTime || !moment.isMoment(currentTime)) return;
      let updatedTime = currentTime.clone();
      if (action === TimeUpdateAction.ADD) {
        updatedTime.add(1, 'minute');
        if (updatedTime.isSameOrAfter(maxTime)) {
          updatedTime = maxTime.clone().subtract(1, 'second');
        }
      } else {
        updatedTime.subtract(1, 'minute');
        if (updatedTime.isSameOrBefore(minTime)) {
          updatedTime = minTime.clone().add(1, 'second');
        }
      }
      updateTime(updatedTime);
    },
    [currentTime, minTime, maxTime, updateTime],
  );

  const handleChangeSeconds = useCallback(
    (action: TimeUpdateAction) => {
      if (!currentTime || !moment.isMoment(currentTime)) return;
      let updatedTime = currentTime.clone();
      if (action === TimeUpdateAction.ADD) {
        updatedTime.add(1, 'second');
        if (updatedTime.isSameOrAfter(maxTime)) {
          updatedTime = maxTime.clone().subtract(1, 'second');
        }
      } else {
        updatedTime.subtract(1, 'second');
        if (updatedTime.isSameOrBefore(minTime)) {
          updatedTime = minTime.clone().add(1, 'second');
        }
      }
      updateTime(updatedTime);
    },
    [currentTime, minTime, maxTime, updateTime],
  );

  const handleChangeMeridiem = useCallback(() => {
    if (!currentTime || !moment.isMoment(currentTime)) return;
    const updatedTime = currentTime.clone();
    const isPM = updatedTime.hours() >= 12;
    const nextTime = isPM
      ? updatedTime.clone().subtract(12, 'hours')
      : updatedTime.clone().add(12, 'hours');

    if (nextTime.isAfter(minTime) && nextTime.isBefore(maxTime)) {
      updateTime(nextTime);
    }
  }, [currentTime, minTime, maxTime, updateTime]);

  const isPM = currentTime && moment.isMoment(currentTime) ? currentTime.hours() >= 12 : false;

  return (
    <>
      <Box
        sx={{
          backgroundColor: FORTA_BLUE.light3,
          width: 222,
          borderRadius: '4px',
          py: isEditing ? 0.5 : 1,
          px: isEditing ? 0.8 : 2,
          '&:hover': disabled
            ? {}
            : {
                cursor: 'pointer',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
              },
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: time === null ? '2px solid red' : 'none',
          ...containerStyles,
        }}
        ref={containerRef}
        onClick={() => {
          if (disabled) return;
          if (!isEditing && (!currentTime || !moment.isMoment(currentTime))) {
            setCurrentTime(minTime.clone().add(1, 'second'));
          }
          setIsEditing(true);
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Hours */}
          <Box
            sx={{
              '& > .MuiTypography-root': {
                transition: 'all 0.2s ease-in-out',
              },
              '&:hover': {
                '& > .MuiTypography-root': {
                  fontWeight: isEditing ? 'bold' : 'normal',
                  color: isEditing ? FORTA_BLUE.primary : FORTA_GRAY5,
                },
              },
            }}
          >
            {isEditing && currentTime && moment.isMoment(currentTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeHour(TimeUpdateAction.ADD);
                }}
                disabled={currentTime.isSame(maxTime.clone().subtract(1, 'second'))}
              >
                <KeyboardArrowUp />
              </IconButton>
            )}
            <Typography color={FORTA_GRAY5} fontSize={24} width={31} textAlign="center">
              {currentTime && moment.isMoment(currentTime) ? currentTime.format('hh') : '--'}
            </Typography>
            {isEditing && currentTime && moment.isMoment(currentTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeHour(TimeUpdateAction.SUBTRACT);
                }}
                disabled={currentTime.isSame(minTime.clone().add(1, 'second'))}
              >
                <KeyboardArrowDown />
              </IconButton>
            )}
          </Box>

          <Typography fontSize={24}>:</Typography>

          {/* Minutes */}
          <Box
            sx={{
              '& > .MuiTypography-root': {
                transition: 'all 0.2s ease-in-out',
              },
              '&:hover': {
                '& > .MuiTypography-root': {
                  fontWeight: isEditing ? 'bold' : 'normal',
                  color: isEditing ? FORTA_BLUE.primary : FORTA_GRAY5,
                },
              },
            }}
          >
            {isEditing && currentTime && moment.isMoment(currentTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeMinutes(TimeUpdateAction.ADD);
                }}
                disabled={currentTime.clone().add(1, 'minute').isSameOrAfter(maxTime)}
              >
                <KeyboardArrowUp />
              </IconButton>
            )}
            <Typography color={FORTA_GRAY5} fontSize={24} width={31} textAlign="center">
              {currentTime && moment.isMoment(currentTime) ? currentTime.format('mm') : '--'}
            </Typography>
            {isEditing && currentTime && moment.isMoment(currentTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeMinutes(TimeUpdateAction.SUBTRACT);
                }}
                disabled={currentTime.clone().subtract(1, 'minute').isSameOrBefore(minTime)}
              >
                <KeyboardArrowDown />
              </IconButton>
            )}
          </Box>

          <Typography fontSize={24}>:</Typography>

          {/* Seconds */}
          <Box
            sx={{
              '& > .MuiTypography-root': {
                transition: 'all 0.2s ease-in-out',
              },
              '&:hover': {
                '& > .MuiTypography-root': {
                  fontWeight: isEditing ? 'bold' : 'normal',
                  color: isEditing ? FORTA_BLUE.primary : FORTA_GRAY5,
                },
              },
            }}
          >
            {isEditing && currentTime && moment.isMoment(currentTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeSeconds(TimeUpdateAction.ADD);
                }}
                disabled={currentTime.clone().add(1, 'second').isSameOrAfter(maxTime)}
              >
                <KeyboardArrowUp />
              </IconButton>
            )}
            <Typography color={FORTA_GRAY5} fontSize={24} width={31} textAlign="center">
              {currentTime && moment.isMoment(currentTime) ? currentTime.format('ss') : '--'}
            </Typography>
            {isEditing && currentTime && moment.isMoment(currentTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeSeconds(TimeUpdateAction.SUBTRACT);
                }}
                disabled={currentTime.clone().subtract(1, 'second').isSameOrBefore(minTime)}
              >
                <KeyboardArrowDown />
              </IconButton>
            )}
          </Box>

          {/* AM/PM */}
          <Box
            sx={{
              '& > .MuiTypography-root': {
                transition: 'all 0.2s ease-in-out',
              },
              '&:hover': {
                '& > .MuiTypography-root': {
                  fontWeight: isEditing ? 'bold' : 'normal',
                  color: isEditing ? FORTA_BLUE.primary : FORTA_GRAY5,
                },
              },
            }}
          >
            {isEditing && currentTime && moment.isMoment(currentTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeMeridiem();
                }}
                disabled={
                  (isPM && currentTime.clone().subtract(12, 'hours').isSameOrBefore(minTime)) ||
                  (!isPM && currentTime.clone().add(12, 'hours').isSameOrAfter(maxTime))
                }
              >
                <KeyboardArrowUp />
              </IconButton>
            )}
            <Typography color={FORTA_GRAY5} fontSize={22} width={31} textAlign="center">
              {currentTime && moment.isMoment(currentTime) ? currentTime.format('A') : '--'}
            </Typography>
            {isEditing && currentTime && moment.isMoment(currentTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeMeridiem();
                }}
                disabled={
                  (isPM && currentTime.clone().subtract(12, 'hours').isSameOrBefore(minTime)) ||
                  (!isPM && currentTime.clone().add(12, 'hours').isSameOrAfter(maxTime))
                }
              >
                <KeyboardArrowDown />
              </IconButton>
            )}
          </Box>

          {/* Delete Button */}
          {isEditing && (
            <Box sx={{ borderLeft: `1px solid ${FORTA_GRAY3}`, pl: 0.5 }}>
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteDialog(true);
                }}
              >
                <TrashIcon width={21} height={21} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <FHConfirmDialog
        open={showDeleteDialog}
        title={'Delete time entry'}
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        contentRenderer={() => (
          <Box display="flex" flexDirection="column" justifyContent="center" px={1} py={6}>
            <Typography textAlign="center" fontSize={20} fontWeight={500}>
              Are you sure you want to delete this time entry?
            </Typography>
            <Typography textAlign="center" fontSize={21} fontWeight={500} mt={1} mb={1}>
              This action cannot be undone.
            </Typography>
          </Box>
        )}
        submittext={'DELETE'}
        canceltext={'CANCEL'}
        onClose={() => setShowDeleteDialog(false)}
        onSubmit={() => {
          setShowDeleteDialog(false);
          onDelete(id);
        }}
      />
    </>
  );
}

export default FHTimeTag;
